import React, { useMemo, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { DataTable } from '../../components/Tables';
import PageHeader from '../shared/PageHeader';
import Button from '../../components/common/Button';
import { invoicesApi, expensesApi, extractList } from '../../api/facades';

type Currency = 'MKW' | 'USD' | 'EUR' | 'GBP' | 'ZAR';

const CURRENCY_SYMBOLS: Record<Currency, string> = {
  MKW: 'MK',
  USD: '$',
  EUR: '€',
  GBP: '£',
  ZAR: 'R',
};

const CURRENCY_NAMES: Record<Currency, string> = {
  MKW: 'Malawian Kwacha',
  USD: 'US Dollar',
  EUR: 'Euro',
  GBP: 'British Pound',
  ZAR: 'South African Rand',
};

const BillingPage: React.FC = () => {
  const [currency, setCurrency] = useState<Currency>(() => {
    const saved = localStorage.getItem('billing-currency');
    return (saved as Currency) || 'MKW';
  });

  useEffect(() => {
    localStorage.setItem('billing-currency', currency);
  }, [currency]);

  const formatCurrency = (amount: number): string => {
    const symbol = CURRENCY_SYMBOLS[currency];
    return `${symbol}${amount.toFixed(2)}`;
  };

  const { data: invoicesRes, isLoading: loadingInvoices } = useQuery({
    queryKey: ['invoices'],
    queryFn: () => invoicesApi.list({}),
  });
  const invoices = extractList(invoicesRes);

  const { data: expensesRes, isLoading: loadingExpenses } = useQuery({
    queryKey: ['expenses'],
    queryFn: () => expensesApi.list({}),
  });
  const expenses = extractList(expensesRes);

  const stats = useMemo(() => {
    const totalInvoices = invoices.reduce((sum: number, inv: any) => sum + (parseFloat(inv.total_amount) || 0), 0);
    const totalExpenses = expenses.reduce((sum: number, exp: any) => sum + (parseFloat(exp.total_amount) || 0), 0);
    const pendingInvoices = invoices.filter((inv: any) => inv.inv_status?.toLowerCase().includes('pending') || inv.inv_status?.toLowerCase().includes('unpaid')).length;
    return { totalInvoices, totalExpenses, net: totalInvoices - totalExpenses, pendingInvoices };
  }, [invoices, expenses]);

  const recentInvoicesColumns = [
    { key: 'invoice_no', label: 'Invoice No.' },
    { key: 'client', label: 'Client', render: (r: any) => r.client?.name ?? r.client_id ?? '—' },
    { key: 'total_amount', label: 'Amount', render: (r: any) => formatCurrency(parseFloat(r.total_amount || 0)) },
    { key: 'inv_status', label: 'Status' },
    { key: 'due_date', label: 'Due Date', render: (r: any) => (r.due_date ? String(r.due_date).slice(0, 10) : '—') },
  ];

  const recentExpensesColumns = [
    { key: 'invoice_no', label: 'Invoice No.' },
    { key: 'vendor_id', label: 'Vendor ID' },
    { key: 'total_amount', label: 'Amount', render: (r: any) => formatCurrency(parseFloat(r.total_amount || 0)) },
    { key: 'inv_status', label: 'Status' },
    { key: 'due_date', label: 'Due Date', render: (r: any) => (r.due_date ? String(r.due_date).slice(0, 10) : '—') },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <PageHeader
          hideTitle
          subtitle="Manage invoices and expenses."
        />
        <div className="flex items-center gap-2">
          <label className="text-sm font-semibold text-slate-700 dark:text-slate-200">
            Currency:
          </label>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value as Currency)}
            className="rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm text-slate-700 dark:text-slate-200 focus:border-primary-300 focus:outline-none"
          >
            {(Object.keys(CURRENCY_SYMBOLS) as Currency[]).map((curr) => (
              <option key={curr} value={curr}>
                {curr} - {CURRENCY_NAMES[curr]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
          <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Total Revenue</div>
          <div className="mt-2 text-2xl font-bold text-slate-700 dark:text-slate-200">
            {formatCurrency(stats.totalInvoices)}
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
          <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Total Expenses</div>
          <div className="mt-2 text-2xl font-bold text-slate-700 dark:text-slate-200">
            {formatCurrency(stats.totalExpenses)}
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
          <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Net</div>
          <div className={`mt-2 text-2xl font-bold ${stats.net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency(stats.net)}
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
          <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Pending Invoices</div>
          <div className="mt-2 text-2xl font-bold text-slate-700 dark:text-slate-200">
            {stats.pendingInvoices}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-200">Recent Invoices</h2>
            <Link to="/dashboard/invoices">
              <Button variant="secondary">View All</Button>
            </Link>
          </div>
          <DataTable
            columns={recentInvoicesColumns}
            data={invoices.slice(0, 5)}
            loading={loadingInvoices}
            emptyMessage="No invoices."
            onRowClick={(id) => window.location.href = `/dashboard/invoices/${id}`}
          />
          <div className="mt-4">
            <Link to="/dashboard/invoices/new">
              <Button variant="primary">Create Invoice</Button>
            </Link>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-200">Recent Expenses</h2>
            <Link to="/dashboard/expenses">
              <Button variant="secondary">View All</Button>
            </Link>
          </div>
          <DataTable
            columns={recentExpensesColumns}
            data={expenses.slice(0, 5)}
            loading={loadingExpenses}
            emptyMessage="No expenses."
            onRowClick={(id) => window.location.href = `/dashboard/expenses/${id}`}
          />
          <div className="mt-4">
            <Link to="/dashboard/expenses/new">
              <Button variant="primary">Create Expense</Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingPage;
